'use client'

import React, { useEffect, useState } from 'react'
import { Star, Send, CheckCircle2, MessageSquare, ThumbsUp } from 'lucide-react'
import { apiAddComment } from '@/apis/offers'
import { toast } from 'react-toastify'

interface Props {
    offer_id: string
    company_id: string
}

const CommentSection = ({ offer_id, company_id }: Props) => {
    const [rating, setRating] = useState(0)
    const [hover, setHover] = useState(0)
    const [comment, setComment] = useState('')
    const [loading, setLoading] = useState(false)
    const [hasRated, setHasRated] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    useEffect(() => {
        const lastRated = localStorage.getItem(`hasRated_${offer_id}`)
        if (lastRated && new Date().getTime() - parseInt(lastRated) < 86400000) {
            setHasRated(true)
        }
    }, [offer_id])

    const labels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (hasRated) {
            toast.info('You have already rated this product today.', { autoClose: 2000 })
            return
        }
        if (!rating && !comment.trim()) {
            toast.error('Please select a rating or add a comment.', { autoClose: 2000 })
            return
        }

        setLoading(true)
        try {
            const res = await apiAddComment(offer_id, company_id, comment, rating.toString())
            if (res.status === 'success') {
                localStorage.setItem(`hasRated_${offer_id}`, new Date().getTime().toString())
                setHasRated(true)
                setSubmitted(true)
                setRating(0)
                setComment('')
                toast.success('Thank you! Your feedback has been submitted.', { autoClose: 2000 })
            } else {
                toast.error('Something went wrong. Please try again.', { autoClose: 2000 })
            }
        } catch {
            toast.error('An error occurred while submitting.', { autoClose: 2000 })
        } finally {
            setLoading(false)
        }
    }

    if (submitted || hasRated) {
        return (
            <div className="flex flex-col items-center justify-center gap-2.5 py-6 text-center">
                <div className="w-11 h-11 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shadow-2xs">
                    <CheckCircle2 size={22} />
                </div>
                <h4 className="text-sm font-bold text-slate-900">Thank You For Your Feedback!</h4>
                <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
                    Your rating and review help other shoppers discover the best deals.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <MessageSquare size={15} className="text-[#00bf8d]" />
                    Rate & Review
                </h3>
                <span className="text-[10px] font-semibold text-slate-400">
                    Verified Feedback
                </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3.5">
                {/* Interactive Star Selection */}
                <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">
                        How would you rate this deal?
                    </label>
                    <div className="flex items-center gap-3">
                        <div
                            className="flex items-center gap-1"
                            onMouseLeave={() => setHover(0)}
                        >
                            {[1, 2, 3, 4, 5].map(star => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setRating(star)}
                                    onMouseEnter={() => setHover(star)}
                                    className="p-0.5 transition-transform hover:scale-110 cursor-pointer"
                                    aria-label={`Rate ${star} star`}
                                >
                                    <Star
                                        size={22}
                                        className={`transition-colors duration-150 ${
                                            star <= (hover || rating)
                                                ? 'text-amber-400 fill-amber-400'
                                                : 'text-slate-200 fill-slate-100 hover:text-amber-300'
                                        }`}
                                    />
                                </button>
                            ))}
                        </div>

                        {(hover || rating) > 0 && (
                            <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-[#00a87a] border border-emerald-200">
                                {labels[hover || rating]}
                            </span>
                        )}
                    </div>
                </div>

                {/* Comment Textarea */}
                <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">
                        Write a review <span className="text-slate-400 font-normal text-[11px]">(optional)</span>
                    </label>
                    <textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder="Share your experience with this offer or discount code..."
                        rows={3}
                        className="w-full px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 border border-slate-200 rounded-xl bg-slate-50/60 focus:bg-white focus:border-[#0d2a3a] focus:ring-1 focus:ring-[#00bf8d] transition-all outline-none resize-none leading-relaxed"
                    />
                </div>

                {/* Submit Button */}
                <div className="pt-1 flex items-center justify-between gap-3">
                    <button
                        type="submit"
                        disabled={loading || (!rating && !comment.trim())}
                        className="inline-flex items-center justify-center gap-1.5 px-5 h-9 bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all shadow-xs active:scale-95 cursor-pointer"
                    >
                        {loading ? (
                            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                <Send size={12} />
                                <span>Submit Feedback</span>
                            </>
                        )}
                    </button>

                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <ThumbsUp size={11} className="text-slate-400" /> Helps shoppers
                    </span>
                </div>
            </form>
        </div>
    )
}

export default CommentSection
